<script setup lang="ts">
import { computed } from 'vue';
import ScheduleBadgePair from '@/components/schedules/ScheduleBadgePair.vue';
import { subscriberZoneBadges } from '@/components/arrayColumns/scheduleCell';
import type { ArrayColumnBadge } from '@/components/arrayColumns';
import { modelIcon } from '@/composables/modelIcons';
import { useOpenContainerSection } from '@/composables/useOpenContainerSection';
import { formatCount } from '@/utils/formatCount';

/**
 * The counts at the right of a bucket or custom list. Each count sits at the
 * end of the line for the thing it counts. For a custom list, the number of
 * zones that subscribe to its zone sits on the first row, beside the zone. The
 * track count sits on the second row, beside the name. The search card and the
 * inspector heading both draw these counts, so the same record reads the same
 * in both places.
 *
 * It lays itself into the caller's grid. That grid must have three columns: the
 * caller's text, then the counts' numbers, then their icons. The caller puts
 * the zone line on row one and the record's name on row two. The numbers
 * right-align, and the icons line up on the right edge.
 *
 * The track count always keeps its place. The backend writes `track_count` 0
 * with a null `track_count_counted_at` for a source it has not counted yet. That
 * source shows "—" rather than "0". A row that carries no count at all shows
 * "—" too. An empty place used to let the subscriber count above drift left.
 *
 * Clicking the track count opens this record's inspector on its Tracks section
 * ({@link useOpenContainerSection}).
 */
// Two roots, each placed in the caller's grid, so there is no single element for stray attributes.
defineOptions({ inheritAttrs: false });

const props = withDefaults(
    defineProps<{
        row: Record<string, unknown>;
        entityId: string;
        /** The card holding the counts opens another record, so the track count opens this one's inspector itself. */
        opensOwnInspector?: boolean;
        /** The grid row the record's name sits on. The track count sits beside it. A vault has no zone row, so its name is on row 1. */
        nameRow?: 1 | 2;
    }>(),
    { opensOwnInspector: false, nameRow: 2 },
);

const isCustomList = computed(() => props.entityId === 'LocationZoneCustomPlaylist');

const trackCountBadges = computed((): ArrayColumnBadge[] => {
    const trackCount = props.row.track_count;
    const counted = typeof trackCount === 'number' && Number.isFinite(trackCount) && props.row.track_count_counted_at !== null;
    const tracks = counted ? `${formatCount(trackCount)} ${trackCount === 1 ? 'track' : 'tracks'}` : 'Not counted yet';
    return [
        {
            key: 'tracks',
            count: counted ? trackCount : 0,
            text: counted ? undefined : '—',
            variant: 'outlined',
            alwaysShow: true,
            icon: modelIcon('LiveTrack') ?? undefined,
            label: `${tracks} · ID ${props.row.id}`,
        },
    ];
});

// How many zones subscribe to the list's zone. Only a custom list draws it.
const subscriberZoneCount = computed(() => subscriberZoneBadges(props.row.location_zone_id));

const openSection = useOpenContainerSection(() => props.opensOwnInspector);

function openTracks(event: MouseEvent) {
    openSection(event, props.entityId, props.row.id, 'tracks');
}
</script>

<template>
    <!-- keydown.stop: the search card cancels Enter and Space to open itself, which would also cancel this button's click.
         data-row-click-opens: the search card ignores clicks on its buttons, but this click must go on to open the card. -->
    <button
        data-row-click-opens
        type="button"
        class="relative col-span-2 col-start-2 grid cursor-pointer grid-cols-subgrid rounded-sm transition-opacity hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring [&_*]:cursor-pointer"
        :class="nameRow === 1 ? 'row-start-1' : 'row-start-2'"
        data-testid="card-track-count"
        @click="openTracks"
        @keydown.stop
    >
        <ScheduleBadgePair :badges="trackCountBadges" layout="bare" columns />
    </button>
    <ScheduleBadgePair
        v-if="isCustomList"
        class="col-start-2 row-start-1"
        :badges="subscriberZoneCount"
        layout="bare"
        columns
        data-testid="container-zone-subscriber-count"
    />
</template>
